import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ShoppingCart, Plus, Trash2, Search, Copy, Share2, X } from "lucide-react";
import { api } from "@/lib/auth";
import { toast } from "sonner";

export default function MenuDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shopping, setShopping] = useState(null);
  const [shopLoading, setShopLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api().get(`/menus/${id}`);
      setM(res.data);
    } catch { toast.error("Menú no encontrado"); nav("/menus"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const buildShoppingList = async () => {
    setShopLoading(true);
    try {
      const res = await api().get(`/menus/${id}/shopping-list`);
      setShopping(res.data);
    } catch { toast.error("No pudimos generar la lista"); }
    finally { setShopLoading(false); }
  };

  const removeRecipe = async (slug) => {
    const slugs = (m.recipe_slugs || []).filter((s) => s !== slug);
    await api().patch(`/menus/${id}`, { recipe_slugs: slugs });
    setShopping(null); load();
  };

  const copyShoppingList = () => {
    if (!shopping) return;
    const txt = `🛒 ${shopping.menu_name}\n\n` +
      groupBy(shopping.items, "seccion").map(([sec, items]) =>
        `▸ ${sec}\n` + items.map((i) => `  • ${i.texto}`).join("\n")
      ).join("\n\n") + `\n\n— ${shopping.recipe_count} recetas`;
    navigator.clipboard.writeText(txt).then(() => toast.success("Lista copiada"));
  };

  const shareShoppingList = async () => {
    if (!shopping) return;
    const txt = `🛒 ${shopping.menu_name}\n\n` +
      groupBy(shopping.items, "seccion").map(([sec, items]) =>
        `▸ ${sec}\n` + items.map((i) => `  • ${i.texto}`).join("\n")
      ).join("\n\n");
    if (navigator.share) {
      try { await navigator.share({ title: shopping.menu_name, text: txt }); } catch {}
    } else { copyShoppingList(); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-cocoa font-serif italic">Cargando…</div>;
  if (!m) return null;

  return (
    <div className="min-h-screen paper-texture iphone-safe-top">
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/menus" className="p-2 rounded-full bg-cream border border-line"><ChevronLeft className="w-5 h-5 text-cocoa" /></Link>
          <h1 className="font-display italic text-cocoa text-base sm:text-xl line-clamp-1 max-w-[60vw]">{m.name}</h1>
          <button data-testid="add-recipe-to-menu-btn" onClick={() => setShowAdd(true)} className="p-2 rounded-full bg-cream border border-line"><Plus className="w-5 h-5 text-cocoa" /></button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pt-6 pb-32">
        <p className="eyebrow mb-3">{m.recipes?.length || 0} recetas en este menú</p>

        <ul data-testid="menu-recipes-list" className="space-y-3 mb-8">
          {(m.recipes || []).map((r) => (
            <li key={r.slug} className="bg-cream border border-line rounded-lg p-3 flex items-center gap-3">
              {r.photo ? (
                <img src={r.photo} alt="" className="w-16 h-16 object-cover rounded shrink-0" />
              ) : (
                <div className="w-16 h-16 bg-paper-warm rounded shrink-0" />
              )}
              <Link to={`/receta/${r.slug}`} className="flex-1 min-w-0">
                <p className="eyebrow text-[10px] line-clamp-1">{r.categoria}</p>
                <h3 className="font-display italic text-cocoa text-lg line-clamp-2 leading-tight">{r.nombre}</h3>
              </Link>
              <button data-testid={`menu-remove-recipe-${r.slug}`} onClick={() => removeRecipe(r.slug)} className="p-2 text-cocoa-soft hover:text-amber-deep"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>

        {(m.recipes || []).length === 0 && (
          <div className="text-center py-12 bg-cream border border-line rounded-lg mb-8">
            <p className="font-serif italic text-cocoa-soft mb-4">Tu menú está vacío.</p>
            <button data-testid="empty-add-recipes-btn" onClick={() => setShowAdd(true)} className="btn-amber"><Plus className="w-4 h-4" /> Agregar recetas</button>
          </div>
        )}

        {(m.recipes || []).length > 0 && (
          <div className="flex justify-center mb-6">
            <button data-testid="generate-shopping-btn" onClick={buildShoppingList} disabled={shopLoading} className="btn-primary">
              <ShoppingCart className="w-5 h-5" /> {shopLoading ? "Calculando…" : (shopping ? "Recalcular lista" : "Generar lista de compras")}
            </button>
          </div>
        )}

        {shopping && (
          <section data-testid="shopping-list" className="bg-cream border border-line rounded-lg p-5 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display italic text-cocoa text-2xl">Lista de Compras</h2>
              <div className="flex gap-2">
                <button data-testid="copy-shopping-btn" onClick={copyShoppingList} className="p-2 rounded-full bg-paper border border-line"><Copy className="w-4 h-4 text-cocoa" /></button>
                <button data-testid="share-shopping-btn" onClick={shareShoppingList} className="p-2 rounded-full bg-paper border border-line"><Share2 className="w-4 h-4 text-cocoa" /></button>
              </div>
            </div>
            {groupBy(shopping.items, "seccion").map(([section, items]) => (
              <div key={section} className="mb-5 last:mb-0">
                <p className="eyebrow mb-2">{section}</p>
                <ul className="space-y-1.5">
                  {items.map((it, i) => (
                    <li key={i} className="font-serif text-cocoa flex gap-2">
                      <span className="text-amber-deep">·</span><span>{it.texto}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}
      </main>

      {showAdd && <AddRecipeToMenuModal menuId={id} existingSlugs={m.recipe_slugs || []} onClose={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function groupBy(items, key) {
  const map = new Map();
  for (const it of items || []) {
    const k = it[key] || "Otros";
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(it);
  }
  return Array.from(map.entries());
}

function AddRecipeToMenuModal({ menuId, existingSlugs, onClose }) {
  const [q, setQ] = useState("");
  const [recipes, setRecipes] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);

  const search = async (qry) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (qry) params.set("q", qry);
    params.set("limit", "60");
    try {
      const res = await api().get(`/recipes?${params.toString()}`);
      setRecipes(res.data.recipes);
    } finally { setLoading(false); }
  };

  useEffect(() => { search(""); }, []);
  useEffect(() => { const t = setTimeout(() => search(q), 250); return () => clearTimeout(t); }, [q]);

  const save = async () => {
    const newSlugs = Object.keys(selected).filter((s) => selected[s]);
    if (newSlugs.length === 0) { onClose(); return; }
    const all = Array.from(new Set([...existingSlugs, ...newSlugs]));
    await api().patch(`/menus/${menuId}`, { recipe_slugs: all });
    toast.success(`${newSlugs.length} receta(s) agregada(s)`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-cocoa/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-cream rounded-t-2xl sm:rounded-2xl border border-line w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-line flex items-center justify-between">
          <h3 className="font-display italic text-cocoa text-2xl">Agregar recetas al menú</h3>
          <button data-testid="close-add-modal" onClick={onClose} className="p-2 rounded-full bg-paper border border-line"><X className="w-4 h-4 text-cocoa" /></button>
        </div>
        <div className="p-4 border-b border-line">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cocoa-soft" />
            <input data-testid="modal-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar recetas…" className="w-full pl-10 pr-4 py-2.5 bg-paper border border-line rounded-full font-serif text-cocoa" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && <p className="font-serif italic text-cocoa-soft text-center py-8">Buscando…</p>}
          <ul className="space-y-2">
            {recipes.map((r) => {
              const already = existingSlugs.includes(r.slug);
              return (
                <li key={r.slug}>
                  <button
                    data-testid={`add-modal-recipe-${r.slug}`}
                    disabled={already}
                    onClick={() => setSelected((s) => ({ ...s, [r.slug]: !s[r.slug] }))}
                    className={`w-full text-left p-3 rounded border transition flex items-center gap-3 ${already ? "border-line bg-paper-warm opacity-50" : selected[r.slug] ? "border-amber-deep bg-amber-light/20" : "border-line bg-paper hover:border-amber-deep"}`}
                  >
                    <span className={`w-5 h-5 rounded-full border-2 shrink-0 ${selected[r.slug] || already ? "bg-amber-deep border-amber-deep" : "border-cocoa-soft"}`} />
                    {r.photo && <img src={r.photo} className="w-12 h-12 object-cover rounded" alt="" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-cocoa font-semibold line-clamp-1">{r.nombre}</h4>
                      <p className="text-xs text-cocoa-soft line-clamp-1">{r.categoria}</p>
                    </div>
                    {already && <span className="text-xs italic text-amber-deep">Ya está</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="p-4 border-t border-line">
          <button data-testid="add-modal-save-btn" onClick={save} className="btn-primary w-full">
            Agregar al menú ({Object.values(selected).filter(Boolean).length})
          </button>
        </div>
      </div>
    </div>
  );
}
